/**
 * Route copy for `src/routes/ai` (keys: `routes.ai.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  abCopyAbCopyPage: {
    delete: 'Delete',
    deleteName: 'Delete {{name}}',
    deleteTheTestName: 'Delete the A/B test “{{name}}”? Its variants and measurements will be lost.',
    testDeleted: 'Test deleted.',
    couldNotDelete: 'Could not delete.',
    couldNotLoadTheAB: 'Could not load the A/B tests.',
    enterANameAndABase: 'Enter a name and a base prompt.',
    numvariantsVariantsGenerated: '{{numVariants}} variants generated.',
    couldNotCreate: 'Could not create.',
    measurementsSaved: 'Measurements saved.',
    couldNotSaveTheMeasurements: 'Could not save the measurements.',
    winnerDesignated: 'Winner designated.',
    couldNotDecideMissingMeasurements: 'Could not decide (missing measurements?).',
    aBCopyTests: 'A/B copy tests',
    generateVariantsMeasureTheirPerformanceL: 'Generate variants, measure their performance, let the AI designate the winner.',
    tests: 'Tests',
    inProgress: 'In progress',
    decided: 'Decided',
    totalVariants: 'Total variants',
    newTest: 'New test',
    testName: 'Test name',
    eGProductLaunchHook: 'E.g. Product launch hook',
    variants: 'Variants',
    metric: 'Metric',
    basePrompt: 'Base prompt',
    describeTheMessageToTurnInto: 'Describe the message to turn into variants…',
    dictate: 'Dictate',
    generating: 'Generating…',
    generateTheVariants: 'Generate the variants',
    loadingTests: 'Loading tests…',
    noABTest: 'No A/B test',
    createYourFirstTestTheAi: 'Create your first test: the AI turns your prompt into several variants you can compare in the field.',
    variantV: 'Variant {{v}}',
    winner: 'Winner',
    scoreScore: 'score {{score}}',
    enterTheMeasurements: 'Enter the measurements',
    designateTheWinner: 'Designate the winner',
    measurementsName: 'Measurements — {{name}}',
    impressions: 'Impressions',
    clicksInteractions: 'Clicks / interactions',
    cancel: 'Cancel',
    saving: 'Saving…',
    saveTheMeasurements: 'Save the measurements',
    aBLoop: 'A/B loop',
    generatePublishTheVariantsComeBack: 'Generate, publish the variants, come back to enter impressions and interactions, then let the service compute the winner based on the chosen metric.',
  },
  brandVoiceBrandVoicePage: {
    couldNotLoadTheVoiceProfiles: 'Could not load the voice profiles.',
    nameTheProfileAndPasteAt: 'Name the profile and paste at least one sample post.',
    voiceProfileTrained: 'Voice profile trained.',
    trainingNotPossible: 'Training not possible.',
    nameTheProfileAndEnterAt: 'Name the profile and enter at least one URL.',
    voiceLearnedFromTheProvidedPages: 'Voice learned from the provided pages.',
    couldNotLearnFromTheUrl: 'Could not learn from the URL.',
    pasteAtLeastOnePostTo: 'Paste at least one post to retrain.',
    profileRetrained: 'Profile retrained.',
    retrainingNotPossible: 'Retraining not possible.',
    deleteTheProfileName: 'Delete the profile “{{name}}”?',
    profileDeleted: 'Profile deleted.',
    couldNotDelete: 'Could not delete.',
    chooseAProfileAndEnterA: 'Choose a profile and enter a draft.',
    scoringNotPossible: 'Scoring not possible.',
    brandVoice: 'Brand voice',
    trainAVoiceProfileFromYour: 'Train a voice profile from your posts, then score your drafts.',
    profiles: 'Profiles',
    samples: 'Samples',
    active: 'Active',
    latestScore: 'Latest score',
    trainScoreOrDeleteABrand: 'train, score or delete a brand voice',
    trainAProfile: 'Train a profile',
    fromPosts: 'From posts',
    fromAUrl: 'From a URL',
    profileName: 'Profile name',
    eGLinkedinVoiceFounder: 'E.g. LinkedIn voice — founder',
    samplePosts: 'Sample posts',
    separateEachPostWithAnEmpty: 'Separate each post with an empty line. {{length}} detected.',
    post1Post2Post3: 'Post 1…\n\nPost 2…\n\nPost 3…',
    training: 'Training…',
    trainTheVoice: 'Train the voice',
    publicPageUrlS: 'Public page URL(s)',
    oneOrMoreUrlsSeparatedBy: 'One or more URLs (separated by a space, a comma or a line) whose text defines the voice.',
    learning: 'Learning…',
    learnFromTheUrl: 'Learn from the URL',
    scoreADraft: 'Score a draft',
    referenceProfile: 'Reference profile',
    noProfile: 'No profile',
    draftToAssess: 'Draft to assess',
    pasteADraftToMeasureHow: 'Paste a draft to measure how close it is to the voice…',
    scoring: 'Scoring…',
    score: 'Score',
    voiceProximity: 'Voice proximity',
    verdictVerdict: 'Verdict: {{verdict}}',
    loadingProfiles: 'Loading profiles…',
    noVoiceProfile: 'No voice profile',
    trainYourFirstProfilePaste3: 'Train your first profile: paste 3 to 5 posts representative of your style.',
    active2: 'Active',
    inactive: 'Inactive',
    sampleS: 'sample(s)',
    cancel: 'Cancel',
    retrain: 'Retrain',
    delete: 'Delete',
    newSamplePostsSeparatedByAn: 'New sample posts…\n\n(separated by an empty line)',
    retraining: 'Retraining…',
    recomputeTheVoice: 'Recompute the voice',
    whereItIsReused: 'Where it is reused',
    onceTrainedAProfileCanBe: 'Once trained, a profile can be injected into the Content studio and Repurposing, and serves as the reference to automatically score your drafts\' compliance.',
  },
  canvasCanvasPage: {
    expand: 'Expand',
    shorten: 'Shorten',
    lengthen: 'Lengthen',
    improve: 'Improve',
    continue: 'Continue',
    seoAnalysisNotPossible: 'SEO analysis not possible.',
    nothingToProcessSelectTextOr: 'Nothing to process — select text or place the cursor in a section.',
    emptyDocumentNothingToContinue: 'Empty document — nothing to continue.',
    continuationInserted: 'Continuation inserted.',
    textReplaced: 'Text replaced.',
    aiActionNotPossible: 'AI action not possible.',
    aiModelTemporarilyUnavailable: 'AI model temporarily unavailable.',
    insufficientAiCredits: 'Insufficient AI credits.',
    documentCopied: 'Document copied.',
    couldNotCopy: 'Could not copy.',
    documentAndLocalDraftCleared: 'Document and local draft cleared.',
    localDraftIgnoredTheDocumentStays: 'Local draft ignored (the document stays as it is).',
    canvas: 'Canvas',
    aLongDocumentASectionSidebar: 'A long document, a section sidebar, inline AI actions on the selection, and a live SEO score.',
    sections: 'Sections',
    headings: 'Headings # / ## / ###',
    addHeadings: 'Add headings',
    words: 'Words',
    lengthSeoKeywordS: '{{length}} SEO keyword(s)',
    seoScore: 'SEO score',
    analyzing: 'Analyzing…',
    upToDateWithTheText: 'Up to date with the text',
    writeToAnalyze: 'Write to analyze',
    localDraft: 'Local draft',
    localAutosave: 'Local autosave',
    localDraftRestoredSavedOn: 'Local draft restored (saved on',
    clearTheLocalDraft: 'Clear the local draft',
    aiSettings: 'AI settings',
    toneOptional: 'Tone (optional)',
    eGExpertEducationalConversational: 'E.g. expert, educational, conversational…',
    expertAndEducational: 'expert and educational',
    brandVoiceOptional: 'Brand voice (optional)',
    none: 'None',
    seoKeywordsOptional: 'SEO keywords (optional)',
    commaSeparatedLengthDetected: 'Comma-separated. {{length}} detected.',
    crmMigrationSmbSoftware: 'crm migration, smb software',
    documentOutline: 'Document outline',
    noSections: 'No sections',
    addMarkdownHeadingsTitleSubtitleTo: 'Add Markdown headings (# Title, ## Subtitle) to structure the document — they will appear here as a clickable outline.',
    document: 'Document',
    copy: 'Copy',
    erase: 'Erase',
    continuesWritingAfterTheCursorOr: 'Continues writing after the cursor (or the end of the document).',
    actsOnTheSelectionOrOn: 'Acts on the selection, or on the section under the cursor if nothing is selected.',
    documentMarkdown: 'Document (markdown)',
    documentTitleStartWritingOrUse: '# Document title\n\nStart writing, or use “Continue” to let the AI begin…',
    selectTextThenChooseAnAction: 'Select text, then choose an action, or place the cursor in a section without selecting anything — the action will apply to that whole section. “Continue” adds the continuation right after the cursor.',
    aiActionsComingSoon: 'AI actions coming soon',
    longFormGenerationIsNotConfigured: 'Long-form generation is not configured on this backend yet — the Canvas AI actions (which reuse it) are not available for now.',
    seoScoreWholeDocument: 'SEO score (whole document)',
    nothingToAnalyze: 'Nothing to analyze',
    writeInTheDocumentAboveThe: 'Write in the document above — the SEO score updates automatically.',
    seoScoreComingSoon: 'SEO score coming soon',
    seoScoringIsNotConfiguredOn: 'SEO scoring is not configured on this backend yet.',
    realVsHeuristic: 'Real vs heuristic',
    everyAiActionExpandShortenLengthen: 'Every AI action (expand, shorten, lengthen, improve, continue) goes through the same real model as the Long-Form Studio: when it is unavailable, you will see an honest error (503), never made-up text — and every generation is deducted from your AI credits. The SEO score assesses the text actually present in the editor above, continuously. The document is saved locally (this browser) as you type; reload the page to check that it is restored.',
    restored: 'Restored',
    saved: 'Saved',
    empty: 'Empty',
  },
  communityAiCommunityAiPage: {
    pasteTheInteractionText: 'Paste the interaction text.',
    draftGenerated: 'Draft generated.',
    generatingTheDraftFailed: 'Generating the draft failed.',
    draftCopied: 'Draft copied.',
    couldNotCopy: 'Could not copy.',
    aiCommunityManager: 'AI Community Manager',
    generateAReplyDraftWithGuardrails: 'Generate a reply draft with guardrails. Nothing is published automatically.',
    confidence: 'Confidence',
    intent: 'Intent',
    sentiment: 'Sentiment',
    recommendedAction: 'Recommended action',
    interactionToHandle: 'Interaction to handle',
    messageReceived: 'Message received',
    eGYourDeliveryIs5: 'E.g. Your delivery is 5 days late, this is unacceptable…',
    author: 'Author',
    handle: '@handle',
    platform: 'Platform',
    type: 'Type',
    brandContextFaqOptional: 'Brand context / FAQ (optional)',
    brandToneAnswerElementsReturnPolicy: 'Brand tone, answer elements, return policy…',
    generating: 'Generating…',
    generateTheDraft: 'Generate the draft',
    noDraftYet: 'No draft yet',
    pasteAnInteractionCommentDmMention: 'Paste an interaction (comment, DM, mention) above, then run “Generate the draft”: the AI suggests a reply classified by intent and sentiment, for you to copy yourself.',
    suggestedDraft: 'Suggested draft',
    actionRecommendedAction: 'action: {{recommended_action}}',
    sentimentSentiment: 'sentiment: {{sentiment}}',
    intentIntent: 'intent: {{intent}}',
    modelConfidence: 'Model confidence',
    copyTheDraft: 'Copy the draft',
    modelModel: 'Model: {{model}}',
    runV: ' · run {{v}}',
    guardrails: 'Guardrails',
    everyDraftIsClassifiedIntentSentiment: 'Every draft is classified (intent, sentiment) and carries safety flags. When the recommended action is “escalate” or “hide”, review before any publication. Sending always stays manual.',
  },
  contentStudioContentStudioPage: {
    fullPost: 'Full post',
    carousel: 'Carousel',
    thread: 'Thread',
    ideas: 'Ideas',
    videoScript: 'Video script',
    carouselCopy: 'Carousel (copy)',
    models: 'Models',
    enterATopic: 'Enter a topic.',
    contentGeneratedInDegradedModeThe: 'Content generated in degraded mode: the AI model is temporarily unavailable.',
    generationComplete: 'Generation complete.',
    generationFailed: 'Generation failed.',
    nothingToSaveGenerateContentOr: 'Nothing to save: generate content or attach a media.',
    sessionExpiredSignInAgainTo: 'Session expired — sign in again to save a draft.',
    studioModeLabel: 'Studio · {{MODE_LABEL}}',
    visualOnly: '(visual only)',
    draftCreatedWithTheAttachedMedia: 'Draft created with the attached media.',
    savingTheDraftFailed: 'Saving the draft failed.',
    contentStudio: 'Content studio',
    generatePostsCarouselsAndThreadsGrounded: 'Generate posts, carousels and threads grounded in your hooks and your brand voice.',
    generations: 'Generations',
    thisSession: 'This session',
    mode: 'Mode',
    platform: 'Platform',
    latestVoiceScore: 'Latest voice score',
    contentType: 'Content type',
    ideas2: '💡 Ideas',
    videoScript2: '🎬 Video script',
    carouselCopy2: '🖼️ Carousel (copy)',
    templates: '📚 Templates',
    topic: 'Topic',
    eGThe3MistakesKilling: 'E.g. the 3 mistakes killing your LinkedIn reach in 2026',
    dictate: 'Dictate',
    brandVoiceOptional: 'Brand voice (optional)',
    none: 'None',
    primeWithTheHookLibrary: 'Prime with the hook library',
    creativeBriefOptional: 'Creative brief (optional)',
    visuals: 'Visuals',
    addAVisual: '+ Add a visual',
    uploadGenerateAiOrImportAn: 'Upload, generate (AI) or import an image/video to attach to this production.',
    generating: 'Generating…',
    generate: 'Generate',
    saving: 'Saving…',
    createADraft: 'Create a draft',
    draftSavedLengthMedia: 'Draft saved ({{length}} media',
    openInTheContentEditor: 'Open in the content editor',
    degradedModeAiModelUnavailableThis: '⚠️ Degraded mode — AI model unavailable. This content was produced by a deterministic fallback generator (not the model): rerun the generation later for a fully AI result.',
    generatedPost: 'Generated post',
    voiceScoreV: 'Voice score: {{v}}%',
    carouselVSlides: 'Carousel · {{v}} slides',
    threadVSegments: 'Thread · {{v}} segments',
    tip: 'Tip',
    theStudioAutomaticallyInjectsYourBest: 'The studio automatically injects your best hooks (via the Hook library) and, if you trained one, your Brand voice. The richer these two sources, the more on-brand the output.',
  },
  contentStudioStudioTools: {
    enterATopic: 'Enter a topic.',
    ideaGenerationFailed: 'Idea generation failed.',
    topicTheme: 'Topic / theme',
    eGMarketingAutomationForSmbs: 'E.g. marketing automation for SMBs',
    platform: 'Platform',
    numberOfIdeas: 'Number of ideas',
    audienceOptional: 'Audience (optional)',
    generating: 'Generating…',
    generateIdeas: 'Generate ideas',
    angleAngle: 'Angle: {{angle}}',
    sendToTheComposer: 'Send to the composer',
    scriptGenerationFailed: 'Script generation failed.',
    videoTopic: 'Video topic',
    eG3TipsToSave: 'E.g. 3 tips to save time with AI',
    format: 'Format',
    generateTheScript: 'Generate the script',
    sceneCountSceneSEstimatedDuration: '· {{scene_count}} scene(s) · ~{{estimated_duration_seconds}}s',
    sceneVDurationSecondsS: 'Scene {{v}} · {{duration_seconds}}s',
    visual: 'Visual:',
    voiceOver: 'Voice-over:',
    onScreenText: 'On-screen text:',
    carouselGenerationFailed: 'Carousel generation failed.',
    carouselTopic: 'Carousel topic',
    eGTheStepsOfA: 'E.g. the steps of a good customer onboarding',
    numberOfSlides312: 'Number of slides (3-12)',
    generateTheCopy: 'Generate the copy',
    slideCountSlides: '· {{slide_count}} slides',
    humanizingFailed: 'Humanizing failed.',
    detectionFailed: 'Detection failed.',
    probablyAi: 'Probably AI',
    uncertain: 'Uncertain',
    probablyHuman: 'Probably human',
    textTooShort: 'Text too short',
    qualityToolsHumanizeDetectAi: 'Quality tools — humanize & detect AI',
    textToAnalyzeHumanize: 'Text to analyze / humanize',
    humanizing: 'Humanizing…',
    humanize: 'Humanize',
    analyzing: 'Analyzing…',
    detectAi: 'Detect AI',
    humanizedAiScoreV: 'Humanized · AI score {{v}}% →',
    notHumanizedReason: 'Not humanized: {{reason}}',
    aiProbabilityV: 'AI probability: {{v}}%',
    couldNotLoadTheTemplates: 'Could not load the templates.',
    nameTheTemplate: 'Name the template.',
    templateCreated: 'Template created.',
    couldNotCreate: 'Could not create.',
    couldNotDelete: 'Could not delete.',
    category: 'Category',
    all: 'All',
    newTemplate: 'New template',
    name: 'Name',
    typePost: 'type (post)',
    contentType: 'Content type',
    create: 'Create',
    loadingTemplates: 'Loading templates…',
    deleteName: 'Delete {{name}}',
    system: 'System',
    lengthSectionSToneTone: '{{length}} section(s) · tone {{tone}}',
    preview: 'Preview',
    use: 'Use',
    structureName: 'Structure · {{name}}',
    exampleExample: 'Example: {{example}}',
  },
  ghostwriterGhostwriterPage: {
    french: 'French',
    english: 'English',
    spanish: 'Spanish',
    german: 'German',
    italian: 'Italian',
    portuguese: 'Portuguese',
    dutch: 'Dutch',
    frameworksUnavailable: 'Frameworks unavailable.',
    enterATopicOrABrief: 'Enter a topic or a brief.',
    postGenerated: 'Post generated.',
    couldNotGenerate: 'Could not generate.',
    aiModelTemporarilyUnavailable: 'AI model temporarily unavailable.',
    insufficientAiCredits: 'Insufficient AI credits.',
    noTextToRewrite: 'No text to rewrite.',
    rewriteReady: 'Rewrite ready.',
    rewriteNotPossible: 'Rewrite not possible.',
    noTextToAnalyze: 'No text to analyze.',
    hookAnalysisNotPossible: 'Hook analysis not possible.',
    noTextToAssess: 'No text to assess.',
    reachPredictionNotPossible: 'Reach prediction not possible.',
    rewriteAppliedToThePost: 'Rewrite applied to the post.',
    noTextToTranslate: 'No text to translate.',
    theTranslationCameBackEmpty: 'The translation came back empty.',
    translationReady: 'Translation ready.',
    translationNotPossible: 'Translation not possible.',
    translationAppliedToThePost: 'Translation applied to the post.',
    postCopied: 'Post copied.',
    couldNotCopy: 'Could not copy.',
    textCopiedPasteItIntoThe: 'Text copied — paste it into the composer.',
    openingTheComposerManualCopyNeeded: 'Opening the composer (manual copy needed).',
    frameworkNameScaffoldHookExampleExample: 'Framework: {{name}}\n{{scaffold}}\n\nHook example: {{example_hook}}\n\nTopic: ',
    frameworkNameAddedToTheBrief: 'Framework “{{name}}” added to the brief.',
    aiGhostwriter: 'AI Ghostwriter',
    oneBriefALongFormPost: 'One brief → a long-form post matched to your voice. Rewrite, analyze the hook, pick a proven framework and estimate the reach.',
    characters: 'Characters',
    limitV: 'Limit {{v}}',
    hookScore: 'Hook score',
    analyzeTheHook: 'Analyze the hook',
    estimatedReach: 'Estimated reach',
    vEstimate: '{{v}} · estimate',
    predictTheReach: 'Predict the reach',
    frameworks: 'Frameworks',
    provenScaffolds: 'Proven scaffolds',
    brief: 'Brief',
    postTopicBrief: 'Post topic / brief',
    describeTheIdeaTheAiWrites: 'Describe the idea; the AI writes a ready-to-publish post.',
    eGLessonsLearnedFromMigrating: 'E.g. Lessons learned from migrating to an in-house CRM — 3 takeaways for an SMB…',
    dictate: 'Dictate',
    platform: 'Platform',
    objective: 'Objective',
    engagementLeadsAwareness: 'engagement, leads, awareness…',
    engagement: 'engagement',
    language: 'Language',
    sampleOfYourWritingOptional: 'Sample of your writing (optional)',
    pasteOneOfYourPostsThe: 'Paste one of your posts: the AI will match its tone (local processing, no embedding).',
    pasteATextRepresentativeOfYour: 'Paste a text representative of your voice…',
    generating: 'Generating…',
    generateThePost: 'Generate the post',
    toneMatchedToYourSample: 'Tone matched to your sample',
    post: 'Post',
    characters2: 'characters',
    exceedsTheLimit: '· exceeds the limit',
    postText: 'Post text',
    copy: 'Copy',
    openTheComposer: 'Open the composer',
    analyzing: 'Analyzing…',
    analyzeTheHook2: 'Analyze the hook',
    estimating: 'Estimating…',
    predictTheReach2: 'Predict the reach',
    noPostYet: 'No post yet',
    writeABriefAboveThenRun: 'Write a brief above, then run the generation; the post will appear here, editable, with the analysis tools. You can also paste your own text to analyze it.',
    startFromABlankText: 'Start from a blank text',
    rewriteInAStyle: 'Rewrite in a style',
    targetStyle: 'Target style',
    eGPunchierMoreFormalStorytelling: 'E.g. punchier, more formal, storytelling…',
    punchier: 'punchier',
    rewriting: 'Rewriting…',
    rewrite: 'Rewrite',
    replaceThePost: 'Replace the post',
    rewriteCopied: 'Rewrite copied.',
    charactersBeforeOriginalCharCount: 'characters · before {{original_char_count}}',
    translateLocalize: 'Translate / Localize',
    targetLanguage: 'Target language',
    aiLocalizationNotAWordFor: 'AI localization (not a word-for-word translation).',
    translating: 'Translating…',
    translate: 'Translate',
    translationCopied: 'Translation copied.',
    hookAnalysis: 'Hook analysis',
    suggestedVariants: 'Suggested variants',
    ai: '(AI)',
    heuristic: '(heuristic)',
    variantCopied: 'Variant copied.',
    indicativeRange: 'Indicative range:',
    estimate: 'Estimate · ',
    confidenceConfidenceDisclaimer: 'confidence {{confidence}} — {{disclaimer}}',
    provenFrameworks: 'Proven frameworks',
    loadingFrameworks: 'Loading frameworks…',
    noFramework: 'No framework',
    theScaffoldCatalogIsNotAvailable: 'The scaffold catalog is not available on this backend.',
    use: 'Use',
    when: 'When:',
    realVsHeuristic: 'Real vs heuristic',
    generationAndRewritingGoThroughA: 'Generation and rewriting go through a real model: when it is unavailable, you will see an honest error (503), never a made-up post — and every generation is deducted from your AI credits. Hook analysis, estimated reach and frameworks are deterministic and work offline; the reach is an',
    estimate2: 'estimate',
    notAGuarantee: ', not a guarantee.',
  },
  ghostwriterModel: {
    high: 'High',
    low: 'Low',
    medium: 'Medium',
    strong: 'Strong',
  },
  gridGridStudioPage: {
    result: 'Result',
    queued: 'Queued',
    inProgress: 'In progress',
    done: 'Done',
    failed: 'Failed',
    generationFinishedWithFailedFailureS: 'Generation finished with {{failed}} failure(s) out of {{total}}.',
    generationComplete: 'Generation complete.',
    addAtLeastOneLine: 'Add at least one line.',
    rowVFillInV2: 'Row {{v}}: fill in {{v2}}.',
    generationStartedForTotalRowsRow: 'Generation started for {{total_rows}} row(s).',
    couldNotStartTheGeneration: 'Could not start the generation.',
    aiModelTemporarilyUnavailable: 'AI model temporarily unavailable.',
    insufficientAiCredits: 'Insufficient AI credits.',
    noResultToCopyForThis: 'No result to copy for this row.',
    resultCopied: 'Result copied.',
    couldNotCopy: 'Could not copy.',
    noResultToCopy: 'No result to copy.',
    lengthResultSCopied: '{{length}} result(s) copied.',
    noResultToExportRunThe: 'No result to export — run the generation first.',
    csvExportDownloaded: 'CSV export downloaded.',
    unavailable: 'Unavailable',
    inProgress2: 'In progress…',
    doneFailedcountFailureS: 'Done ({{failedCount}} failure(s))',
    gridBatchGeneration: 'Grid — batch generation',
    anEditableTableOneRowPer: 'An editable table, one row per piece of content to generate, one-click generation for every row.',
    rows: 'Rows',
    batchStatus: 'Batch status',
    completed: 'Completed',
    failed2: 'Failed',
    generator: 'Generator',
    sharedToneOptional: 'Shared tone (optional)',
    appliedToEveryRow: 'Applied to every row.',
    expertAndWarm: 'expert and warm',
    brandVoiceOptional: 'Brand voice (optional)',
    none: 'None',
    model: 'Model',
    lengthRowS: '— {{length}} row(s)',
    addALine: '+ Add a line',
    copyAll: 'Copy all',
    exportCsv: 'Export CSV',
    status: 'Status',
    labelRowV: '{{label}} (row {{v}})',
    failedNoDetailsProvidedByThe: 'Failed — no details provided by the backend.',
    copy: 'Copy',
    waitingForTheResult: 'Waiting for the result…',
    removeRowV: 'Remove row {{v}}',
    removeTheRow: 'Remove the row',
    launching: 'Launching…',
    generatingVV2: 'Generating ({{v}}/{{v2}})…',
    generateAll: 'Generate all',
    succeededFailedcountFailedOutOfJobtotal: 'succeeded, {{failedCount}} failed out of {{jobTotal}}.',
    batchGenerationComingSoon: 'Batch generation coming soon',
    batchGenerationGridIsNotConfigured: 'Batch generation (“Grid”) is not configured on this backend yet.',
    batchTrackingComingSoon: 'Batch tracking coming soon',
    theBatchWasLaunchedIdentifierGridjobid: 'The batch was launched (identifier {{gridJobId}}), but progress tracking is not configured on this backend yet — the statuses above will not update automatically.',
    generationTrackingWasInterruptedUnstable: 'Generation tracking was interrupted (unstable connection or session expired).',
    resumeTracking: 'Resume tracking',
    realVsHeuristic: 'Real vs heuristic',
    eachRowIsAnIndependentAi: 'Each row is an independent AI generation, deducted from your AI credits: a failed row (503/402/backend error) stays red with its own message — it never prevents the other rows from finishing, and nothing is made up to fill a missing result.',
    error: 'Error',
  },
  hookLibraryHookLibraryPage: {
    couldNotLoadTheHookLibrary: 'Could not load the hook library.',
    enterTheHookText: 'Enter the hook text.',
    hookAdded: 'Hook added.',
    couldNotAdd: 'Could not add.',
    hookDeleted: 'Hook deleted.',
    couldNotDelete: 'Could not delete.',
    noHookRankedForThisFilter: '(no hook ranked for this filter)',
    previewUnavailable: 'Preview unavailable.',
    hookLibrary: 'Hook library',
    capitalizeOnYourBestHooksThey: 'Capitalize on your best hooks; they prime every generation as few-shot examples.',
    hooks: 'Hooks',
    averageScore: 'Average score',
    platformsCovered: 'Platforms covered',
    cumulativeUses: 'Cumulative uses',
    addAHook: 'Add a hook',
    hookText: 'Hook text',
    eGNobodyWillTellYou: 'E.g. Nobody will tell you this about…',
    platform: 'Platform',
    all: 'All',
    scoreV: 'Score ({{v}}%)',
    adding: 'Adding…',
    addTheHook: 'Add the hook',
    fewShotPreview: 'Few-shot preview',
    loadingHooks: 'Loading hooks…',
    noHookYet: 'No hook yet',
    addYourWinningHooksTheStudio: 'Add your winning hooks: the studio and repurposing will inject them automatically to stay in your style.',
    scoreV2: 'score {{v}}%',
    sourceSource: 'source: {{source}}',
    deleteTheHook: 'Delete the hook',
    delete: 'Delete',
    howItIsUsed: 'How it is used',
    hooksAreRankedByScoreAnd: 'Hooks are ranked by score and merged with platform examples. The studio and repurposing take the top 5 to prime the generator\'s prompt.',
  },
  longformLongFormStudioPage: {
    blogArticle: 'Blog article',
    longFormSeoStructuredInSections: 'Long-form SEO, structured in sections.',
    landingPage: 'Landing page',
    conversionOrientedShortSections: 'Conversion-oriented, short sections.',
    longEmail: 'Long email',
    inDepthNurturingNewsletter: 'In-depth nurturing / newsletter.',
    enterABrief: 'Enter a brief.',
    outlineGenerated: 'Outline generated.',
    couldNotGenerateTheOutline: 'Could not generate the outline.',
    aiModelTemporarilyUnavailable: 'AI model temporarily unavailable.',
    insufficientAiCredits: 'Insufficient AI credits.',
    contentGenerated: 'Content generated.',
    couldNotGenerateTheContent: 'Could not generate the content.',
    noContentToAnalyze: 'No content to analyze.',
    seoScoreComputed: 'SEO score computed.',
    seoAnalysisNotPossible: 'SEO analysis not possible.',
    contentCopied: 'Content copied.',
    couldNotCopy: 'Could not copy.',
    textCopiedPasteItIntoThe: 'Text copied — paste it into the composer.',
    openingTheComposerManualCopyNeeded: 'Opening the composer (manual copy needed).',
    longFormStudio: 'Long-Form Studio',
    oneBriefAnSeoOutlineAn: 'One brief → an SEO outline → an article, a landing page or a long email, with an SEO score.',
    type: 'Type',
    outlineSections: 'Outline sections',
    outlineReady: 'Outline ready',
    generateAnOutline: 'Generate an outline',
    words: 'Words',
    targetTargetwords: 'Target {{targetWords}}',
    noTarget: 'No target',
    seoScore: 'SEO score',
    lengthKeywordS: '{{length}} keyword(s)',
    analyzeTheSeo: 'Analyze the SEO',
    brief: 'Brief',
    describeTheTopicTheAngleAnd: 'Describe the topic, the angle and the target audience.',
    eGCompleteGuideToCrm: 'E.g. Complete guide to CRM migration for SMBs — audience: non-technical leaders…',
    toneOptional: 'Tone (optional)',
    eGExpertEducationalConversational: 'E.g. expert, educational, conversational…',
    expertAndEducational: 'expert and educational',
    brandVoiceOptional: 'Brand voice (optional)',
    none: 'None',
    targetLengthWordsOptional: 'Target length (words, optional)',
    seoKeywordsOptional: 'SEO keywords (optional)',
    commaSeparatedLengthDetected: 'Comma-separated. {{length}} detected.',
    crmMigrationSmbSoftwareNoCode: 'crm migration, smb software, no-code crm',
    generatingTheOutline: 'Generating the outline…',
    generateTheOutline: 'Generate the outline',
    generating: 'Generating…',
    generateDirectlyNoOutline: 'Generate directly (no outline)',
    outlineComingSoon: 'Outline coming soon',
    outlineGenerationIsNotConfiguredOn: 'Outline generation is not configured on this backend yet. You can generate the content directly with “Generate directly (no outline)”.',
    proposedOutline: 'Proposed outline',
    lengthSectionS: '{{length}} section(s)',
    generateTheContentFromThisOutline: 'Generate the content from this outline',
    regenerating: 'Regenerating…',
    regenerateThePlan: 'Regenerate the plan',
    content: 'Content',
    wordS: 'word(s)',
    targetTargetwords2: ' · target {{targetWords}}',
    generationComingSoon: 'Generation coming soon',
    longFormContentGenerationIsNot: 'Long-form content generation is not configured on this backend yet.',
    title: 'Title',
    metaDescription: 'Meta description',
    contentMarkdown: 'Content (markdown)',
    copy: 'Copy',
    openTheComposer: 'Open the composer',
    analyzingSeo: 'Analyzing SEO…',
    analyzeTheSeo2: 'Analyze the SEO',
    seoScoreComingSoon: 'SEO score coming soon',
    seoScoringIsNotConfiguredOn: 'SEO scoring is not configured on this backend yet.',
    realVsHeuristic: 'Real vs heuristic',
    theOutlineAndTheWritingGo: 'The outline and the writing go through a real AI model: when it is unavailable, you will see an honest error (503), never made-up text — and every generation is deducted from your AI credits. The SEO score assesses the text actually present in the editor above: edit the content, then rerun “Analyze the SEO” for an up-to-date score.',
  },
  memoryMemoryPage: {
    userSessionNotFoundSignIn: 'User session not found — sign in again.',
    theMemoryServiceIsUnreachable: 'The memory service is unreachable.',
    enterAtLeastTheUserMessage: 'Enter at least the user message.',
    totalExtractedMemoryIesExtracted: '{{total_extracted}} memory(ies) extracted.',
    extractionNotPossible: 'Extraction not possible.',
    aiMemory: 'AI memory',
    whatTheAiRemembersAboutYou: 'What the AI remembers about you to personalize its output.',
    profile: 'Profile',
    preferences: 'Preferences',
    restrictions: 'Restrictions',
    knowledge: 'Knowledge',
    filterByTopicOptional: 'Filter by topic (optional)',
    eGLinkedinB2b: 'E.g. LinkedIn B2B',
    loading: 'Loading…',
    reloadTheMemory: 'Reload the memory',
    loadingTheMemory: 'Loading the memory…',
    memoryEmptyForNow: 'Memory empty for now',
    theAiHasNotMemorizedAnything: 'The AI has not memorized anything yet. Extract memories from a conversation below, or keep using the assistant: the context will grow as you go.',
    learnedAntiPatterns: 'Learned anti-patterns',
    promptInjection: 'Prompt injection',
    extractFromAConversation: 'Extract from a conversation',
    userMessage: 'User message',
    eGIAmTheFounder: 'E.g. I am the founder of a B2B SaaS startup, I hate corporate jargon…',
    assistantReplyOptional: 'Assistant reply (optional)',
    extracting: 'Extracting…',
    extractTheMemories: 'Extract the memories',
    extractedTotalNewNew: 'extracted · {{total_new}} new ·',
    updated: 'updated',
    lengthErrorS: ' · {{length}} error(s)',
    whyItIsUseful: 'Why it is useful',
    memoryCapturesYourProfileYourPreferences: 'Memory captures your profile, your preferences and your no-gos, then injects them into the generation prompt. Result: output that is truer to you, without having to restate everything each time.',
    set: 'Set',
  },
  personalizationPersonalizationPage: {
    helloFirstNameISawThat: 'Hello {{first_name}}, I saw that {{company}} is hiring — shall we talk?',
    enterATemplate: 'Enter a template.',
    personalization: 'Personalization',
    mergeCrmFieldsIntoYourMessages: 'Merge CRM fields into your messages — openers, DMs, sequences.',
    tokensDetected: 'Tokens detected',
    fieldsProvided: 'Fields provided',
    missingTokens: 'Missing tokens',
    status: 'Status',
    rendered: 'Rendered',
    model: 'Model',
    textWithTokens: 'Text with tokens',
    useTokenEGFirstName: 'Use {{token}} (e.g. {{first_name}}).',
    fallbackValueMissingToken: 'Fallback value (missing token)',
    eGThere: 'E.g. there',
    contactFields: 'Contact fields',
    token: 'token',
    value: 'value',
    removeTheField: 'Remove the field',
    addAField: '+ Add a field',
    renderTheMessage: 'Render the message',
    result: 'Result',
    missingTokens2: 'Missing tokens:',
    scaleUp: 'Scale up',
    theSameEngineRendersAWhole: 'The same engine renders a whole list of contacts in one pass (batch endpoint) — ideal to personalize an outreach sequence before sending.',
  },
  ragDocumentsRagDocumentsPage: {
    indexPrice: 'Indexing: 1 credit per file, 5 credits per URL.',
    vKb: '{{v}} KB',
    vMb: '{{v}} MB',
    couldNotLoadYourContentPlans: 'Could not load your content plans.',
    couldNotLoadTheDocuments: 'Could not load the documents.',
    documentSentIndexingInProgress: 'Document sent — indexing in progress.',
    couldNotSendTheDocument: 'Could not send the document.',
    urlSentIndexingInProgress: 'URL sent — indexing in progress.',
    couldNotAddTheUrl: 'Could not add the URL.',
    documentDeleted: 'Document deleted.',
    couldNotDelete: 'Could not delete.',
    reprocessingStarted: 'Reprocessing started.',
    reprocessingNotPossible: 'Reprocessing not possible.',
    searchNotPossible: 'Search not possible.',
    ragDocuments: 'RAG documents',
    indexYourDocumentsByPlanAnd: 'Index your documents by plan and query them in natural language.',
    plans: 'Plans',
    documents: 'Documents',
    indexed: 'Indexed',
    searchResults: 'Search results',
    loadingPlans: 'Loading plans…',
    noContentPlan: 'No content plan',
    ragDocumentsAttachToAContent: 'RAG documents attach to a content plan. First create a plan from the Calendar, then come back to drop your documents.',
    contentPlan: 'Content plan',
    sending: 'Sending…',
    dropADocument: '+ Drop a document',
    addAUrl: 'Add a URL',
    adding: 'Adding…',
    acceptedFormatsPdfDocxDocTxt: 'Accepted formats: PDF, DOCX, DOC, TXT, MD, RTF (max 50 MB). Or add a public web page by its URL — its content is extracted, then indexed in the same corpus.',
    semanticSearch: 'Semantic search',
    askAQuestionAboutYourDocuments: 'Ask a question about your documents…',
    searching: 'Searching…',
    search: 'Search',
    noRelevantPassageFound: 'No relevant passage found.',
    relevance: 'relevance',
    loadingDocuments: 'Loading documents…',
    noDocumentInThisPlan: 'No document in this plan',
    dropAFirstDocumentItWill: 'Drop a first document: it will be extracted, then indexed for search and grounded generation (RAG).',
    chunkCountChunks: ' · {{chunk_count}} chunks',
    indexingFailedProcessingError: 'Indexing failed: {{processing_error}}',
    reprocess: 'Reprocess',
    deleteTheDocument: 'Delete the document',
    delete: 'Delete',
    groundedGenerationRag: 'Grounded generation (RAG)',
    onceIndexedYourDocumentsBecomeA: 'Once indexed, your documents become a source of truth: generation can rely on them for factual, up-to-date posts, and you can query the corpus directly here.',
    httpsExampleComArticle: 'https://example.com/article',
  },
  repurposeRepurposePage: {
    enterAUrl: 'Enter a URL.',
    pasteASourceText: 'Paste a source text.',
    selectAtLeastOnePlatform: 'Select at least one platform.',
    platformCountPlatformSGenerated: '{{platform_count}} platform(s) generated.',
    repurposingFailed: 'Repurposing failed.',
    chooseAPdfOrDocxFile: 'Choose a PDF or DOCX file.',
    repurposingTheDocumentFailed: 'Repurposing the document failed.',
    enterTheVideoUrl: 'Enter the video URL.',
    lengthPostSGeneratedFromThe: '{{length}} post(s) generated from the video.',
    importingTheVideoFailed: 'Importing the video failed.',
    noUsablePostToSendTo: 'No usable post to send to the composer.',
    repurposing: 'Repurposing',
    turnAUrlOrALong: 'Turn a URL or a long text into native posts, one per platform.',
    platformsGenerated: 'Platforms generated',
    keyPoints: 'Key points',
    model: 'Model',
    quality: 'Quality',
    fromAUrl: 'From a URL',
    fromAText: 'From a text',
    videoYoutubeVimeo: 'Video (YouTube / Vimeo)',
    documentPdfDocx: 'Document (PDF / DOCX)',
    articlePageUrl: 'Article / page URL',
    videoUrlYoutubeVimeo: 'Video URL (YouTube / Vimeo)',
    fileToReusePdfDocx: 'File to reuse (PDF / DOCX)',
    sourceText: 'Source text',
    pasteYourBlogArticleTranscriptOr: 'Paste your blog article, transcript or long note here…',
    targetPlatforms: 'Target platforms',
    onlyPublicMetadataTitleThumbnailIs: 'Only public metadata (title, thumbnail) is read — the video is never downloaded and the post links back to the original URL.',
    generating: 'Generating…',
    generateThePosts: 'Generate the posts',
    sourceSummary: 'Source summary',
    generatedPostsLength: 'Generated posts ({{length}})',
    sendToTheComposer: 'Send to the composer',
    importedVideo: 'Imported video',
    videoThumbnail: 'Video thumbnail',
    untitledVideo: 'Untitled video',
    partialMetadata: 'Partial metadata',
    goodToKnow: 'Good to know',
    repurposingReusesYourBrandVoiceAnd: 'Repurposing reuses your brand voice and your hooks to stay consistent from one platform to the next. Each post stays an editable draft — schedule it next from Content or Calendar.',
    degraded: 'Degraded',
    nominal: 'Nominal',
  },
  shared: {
    loading: 'Loading…',
  },
  suggestionsSuggestionsPage: {
    n7Days: '7 days',
    n30Days: '30 days',
    n90Days: '90 days',
    vSuggestionS: '{{v}} suggestion(s).',
    theSuggestionEngineIsUnreachable: 'The suggestion engine is unreachable.',
    aiSuggestions: 'AI suggestions',
    prioritizedRecommendationsBasedOnYourRec: 'Prioritized recommendations based on your recent performance.',
    suggestions: 'Suggestions',
    highPriority: 'High priority',
    period: 'Period',
    analyticsContext: 'Analytics context',
    analysisPeriod: 'Analysis period',
    analyzing: 'Analyzing…',
    generateSuggestions: 'Generate suggestions',
    noSuggestions: 'No suggestions',
    noRecommendationForThisPeriodPublish: 'No recommendation for this period. Publish more and rerun the analysis.',
    suggestionV: 'Suggestion {{v}}',
    howToReadTheSuggestions: 'How to read the suggestions',
    eachRecommendationIsPrioritizedUsingRule: 'Each recommendation is prioritized using rules applied to your best posts and your publishing slots. The context is enriched automatically when your analytics services respond; otherwise, the suggestions stay generic and honest.',
    generic: 'Generic',
    nPosts: '{{n}} posts',
  },
};

export default en;
